// features/common/CachedImage.js
// Image component with in-memory caching to prevent re-fetching on tab switches

import React, { useState, useEffect } from "react";
import {
  getCachedPhotoUrl,
  getCachedPhotoUrlSync,
} from "../../services/photoCache";

/**
 * CachedImage - Drop-in replacement for <img> that caches fetched images
 *
 * @param {object} props
 * @param {string} props.photoId - Unique identifier for the photo (used as cache key)
 * @param {string} props.src - URL to fetch image from (presigned URL or local blob URL)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class name
 * @param {boolean} props.skipCache - If true, bypass cache (useful for local previews)
 * @param {function} props.onClick - Click handler
 * @param {boolean} props.draggable - Whether image is draggable
 * @param {object} props.style - Inline styles
 * @param {string} props.role - ARIA role
 * @param {number} props.tabIndex - Tab index for keyboard navigation
 * @param {function} props.onKeyPress - Key press handler
 */
export const CachedImage = ({
  photoId,
  src,
  alt,
  className,
  skipCache = false,
  onClick,
  draggable,
  style,
  role,
  tabIndex,
  onKeyPress,
  ...rest
}) => {
  // Check if we have a cached version synchronously first
  const initialUrl = skipCache ? src : (getCachedPhotoUrlSync(photoId) || null);
  const [displayUrl, setDisplayUrl] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(!initialUrl && !skipCache);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // If skipCache or already have URL, skip
    if (skipCache) {
      setDisplayUrl(src);
      setIsLoading(false);
      return;
    }

    // If we already have a cached URL, use it
    const cached = getCachedPhotoUrlSync(photoId);
    if (cached) {
      setDisplayUrl(cached);
      setIsLoading(false);
      return;
    }

    // No cache - need to fetch and cache
    let mounted = true;
    setIsLoading(true);

    getCachedPhotoUrl(photoId, src)
      .then((blobUrl) => {
        if (mounted) {
          setDisplayUrl(blobUrl);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setDisplayUrl(src); // Fall back to original URL
          setIsLoading(false);
          setHasError(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [photoId, src, skipCache]);

  // Show loading placeholder while fetching
  if (isLoading) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f0f0",
          minHeight: "100px",
        }}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyPress={onKeyPress}
      >
        <span style={{ color: "#666", fontSize: "12px" }}>Loading...</span>
      </div>
    );
  }

  return (
    <img
      src={displayUrl}
      alt={alt}
      className={className}
      onClick={onClick}
      draggable={draggable}
      style={style}
      role={role}
      tabIndex={tabIndex}
      onKeyPress={onKeyPress}
      {...rest}
    />
  );
};

export default CachedImage;
